import { Link } from "react-router-dom";
import { Phone, Mail, Facebook, Instagram, Youtube, Linkedin } from "lucide-react";
import { BRAND } from "../data/landing";
import { Reveal } from "../components/Reveal";

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21l-6.52 7.46L22 22h-6.875l-4.51-5.79L5.4 22H2.64l6.96-7.96L2 2h7.04l4.06 5.34L18.244 2zm-2.41 18h1.92L7.27 4H5.27l10.564 16z" />
  </svg>
);

const TiktokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.01a8.16 8.16 0 0 0 4.77 1.52V7.08a4.85 4.85 0 0 1-1.84-.39z" />
  </svg>
);

const FaxIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 9V3H8v6" />
    <path d="M8 13H6a2 2 0 0 0-2 2v4h4" />
    <rect x="8" y="13" width="13" height="8" rx="1" />
    <path d="M12 17h.01M16 17h.01" />
  </svg>
);

const NAV_LINKS = [
  { label: "Home", href: "#top" },
  { label: "Services", href: "#services" },
  { label: "Our Work", href: "#work" },
  { label: "Meet the Team", href: "#about" },
  { label: "Contact", href: "#contact" },
  { label: "FAQ", href: "/faq", newTab: true },
];

const ACTION_LINKS = [
  { label: "Client Portal", href: BRAND.clientPortalUrl },
  { label: "Book a Meeting", href: BRAND.bookMeetingUrl },
];

const SOCIALS = [
  { Icon: Facebook, href: BRAND.socials.facebook, label: "Facebook" },
  { Icon: XIcon, href: BRAND.socials.x, label: "X" },
  { Icon: Instagram, href: BRAND.socials.instagram, label: "Instagram" },
  { Icon: TiktokIcon, href: BRAND.socials.tiktok, label: "TikTok" },
  { Icon: Youtube, href: BRAND.socials.youtube, label: "YouTube" },
  { Icon: Linkedin, href: "https://www.linkedin.com/", label: "LinkedIn" },
];

export default function BrandFooter() {
  return (
    <section className="relative text-white pb-6" data-testid="brand-footer">
      {/* Content — solid brand blue */}
      <div className="bg-[#1440E1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8">
            <Reveal className="lg:col-span-5"><BrandIntro /></Reveal>
            <Reveal delay={0.1} className="lg:col-span-3"><FooterNav /></Reveal>
            <Reveal delay={0.2} className="lg:col-span-4"><FooterContact /></Reveal>
          </div>
          <FooterLegal />
        </div>
      </div>
    </section>
  );
}

function BrandIntro() {
  return (
    <div>
      <p className="text-[#000080] text-2xl font-extrabold tracking-tight mb-2">
        Knight Media Group
      </p>
      <p className="text-white/85 text-sm leading-relaxed max-w-md">
        Strategy. Innovation. Growth. Transforming brands through intelligent
        social media management and digital marketing solutions.
      </p>
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {SOCIALS.map(({ Icon, href, label }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="size-10 flex items-center justify-center rounded-full bg-white/12 hover:bg-white hover:text-[#1440E1] text-white transition-all duration-300 hover:scale-110 border border-white/25 shadow-[0_10px_24px_-8px_rgba(0,0,0,0.55),0_2px_6px_-1px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_36px_-10px_rgba(255,255,255,0.55),0_0_0_4px_rgba(255,255,255,0.12)] backdrop-blur-md"
            data-testid={`brand-footer-social-${label.toLowerCase()}`}
          >
            <Icon size={15} />
          </a>
        ))}
      </div>
    </div>
  );
}

function FooterNav() {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-white/65 font-semibold mb-4">
        Navigate
      </p>
      <ul className="grid grid-cols-2 lg:grid-cols-1 gap-x-6 gap-y-2.5">
        {NAV_LINKS.map((l) => (
          <li key={l.label}>
            <a
              href={l.href}
              target={l.newTab ? "_blank" : undefined}
              rel={l.newTab ? "noopener noreferrer" : undefined}
              className="text-white/90 text-sm hover:text-white transition-colors"
              data-testid={`brand-footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterContact() {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.3em] text-white/65 font-semibold mb-4">
        Get in Touch
      </p>
      <div className="space-y-3">
        <ContactRow icon={Phone} value={BRAND.phone} href={BRAND.phoneHref} testId="brand-footer-phone" arial />
        <ContactRow icon={FaxIcon} value={`Fax  ${BRAND.fax}`} href={BRAND.faxHref} testId="brand-footer-fax" arial />
        <ContactRow icon={Mail} value={BRAND.email} href={`mailto:${BRAND.email}`} testId="brand-footer-email" />
      </div>
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {ACTION_LINKS.map((a) => (
          <a
            key={a.label}
            href={a.href}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-kmg inline-flex items-center justify-center bg-white/15 hover:bg-white hover:text-[#1440E1] text-white text-xs font-bold px-4 py-2 border border-white/30 tracking-wide"
            data-testid={`brand-footer-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {a.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function FooterLegal() {
  return (
    <div className="border-t border-white/15 mt-8 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/75">
      <p>© {new Date().getFullYear()} Knight Media Group. All rights reserved.</p>
      <div className="flex items-center gap-6">
        <Link to="/privacy-policy" className="hover:text-white transition-colors" data-testid="brand-footer-privacy">
          Privacy Policy
        </Link>
        <Link to="/terms-and-conditions" className="hover:text-white transition-colors" data-testid="brand-footer-terms">
          Terms of Service
        </Link>
      </div>
    </div>
  );
}

function ContactRow({ icon: Icon, value, href, testId, arial }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap href={href} className="flex items-center gap-2.5 group" data-testid={testId}>
      <span className="size-7 shrink-0 rounded-full bg-white/12 border border-white/20 flex items-center justify-center text-white/90 group-hover:bg-white group-hover:text-[#1440E1] transition-colors">
        <Icon size={14} />
      </span>
      <p
        className="text-white text-sm font-medium leading-snug group-hover:text-white/85 transition"
        style={arial ? { fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif' } : undefined}
      >
        {value}
      </p>
    </Wrap>
  );
}
